import { useState } from "react";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { ItemList } from "@/shared/components/ui/ItemList";
import { ListSearch } from "@/shared/components/ui/ListSearch";
import { Section } from "@/shared/components/ui/Section";
import { SectionAction } from "@/shared/components/ui/SectionAction";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { useForms } from "../hooks/formQueries";
import type { FormSummary } from "../types/formTypes";
import { FormRow } from "./FormRow";

interface FormsListProps {
  onPreview: (form: FormSummary) => void;
  selectedForms?: FormSummary[];
  onToggle?: (form: FormSummary) => void;
  search?: string;
  onSearchChange?: (value: string) => void;
}

export function FormsList({ onPreview, selectedForms, onToggle, search, onSearchChange }: FormsListProps) {
  const { businessId } = useCurrentBusiness();
  const { data: forms = [], isLoading, error, refetch } = useForms();
  const [localSearch, setLocalSearch] = useState("");
  const searchText = search ?? localSearch;
  const setSearch = onSearchChange ?? setLocalSearch;
  const query = searchText.trim().toLowerCase();
  const matches = forms.filter((form) => form.name.toLowerCase().includes(query));
  return (
    <div className="space-y-[26px]">
      <ListSearch label="Search forms" placeholder="Form name" value={searchText} onChange={setSearch} />
      <Section title={onToggle ? "Available forms" : "All forms"}
        count={!isLoading && !error ? `${matches.length} ${matches.length === 1 ? "form" : "forms"}` : undefined}
        action={query ? <SectionAction onClick={() => setSearch("")}>Clear search</SectionAction> : undefined}>
        {!businessId ? <EmptyState title="Forms unavailable" message="Select a business to view its forms." /> :
          error ? <ErrorState message={error.message} onRetry={() => void refetch()} /> :
          <ItemList items={matches} isLoading={isLoading} loadingText="Loading forms..."
            emptyState={{
              title: query ? "No matching forms" : "No forms yet",
              message: query ? "Try another form name." : "Create a form on the Forms website to preview and attach it here.",
              action: query ? { label: "Clear search", variant: "outline", onClick: () => setSearch("") } : undefined,
            }}
            renderItem={(form) => <FormRow form={form} onPreview={() => onPreview(form)}
              selected={selectedForms ? selectedForms.some((selected) => selected.id === form.id) : undefined}
              onToggle={onToggle ? () => onToggle(form) : undefined} searchText={searchText.trim()} />}
          />}
      </Section>
    </div>
  );
}
